/**
 * The draft: a pure, seeded state machine for head-to-head deck construction.
 *
 * Like the game engine (`engine.reduce`), this is a deterministic reducer with
 * no DOM/network dependency, so the *same* code runs authoritatively on the
 * server (the GameRoom Durable Object) and locally against the computer. Its
 * only output is two `DeckBuild`s — the very same shape the classic deck builder
 * produces — which are then fed into the unchanged `createInitialState`. Nothing
 * downstream knows or cares that a deck was drafted rather than built.
 *
 * Format: a shared face-up pool of distinct cards. Players alternate picking one
 * affordable, not-yet-maxed card into their own deck, spending from a shared
 * starting budget; the pool refills after each pick. A player who can no longer
 * afford anything (or taps "done") passes; the draft ends when both have. Every
 * card type is eligible from the first pick.
 */
import { ABILITIES } from "./abilities";
import { cardMaxCopies, cardTypeCost, type CardTypeId, type DeckBuild } from "./cards";
import { packOfAbility, packOfPiece } from "./packs";
import { PIECES } from "./pieces";
import { nextInt } from "./rng";
import { DEFAULT_CONFIG } from "./setup";
import type { AbilityId, BoardConfig, PackId, PieceTypeId, PlayerId } from "./types";

/** Starting points each player drafts with (richer than the classic 30). */
export const DRAFT_BUDGET = 45;

/** How many cards sit face-up in the shared pool at once. */
export const POOL_SIZE = 6;

/** Every deck-buildable card — all deployable pieces plus all abilities. */
export const DRAFTABLE: readonly CardTypeId[] = [
  ...(Object.values(PIECES)
    .filter((p) => p.deployable)
    .map((p) => p.id) as PieceTypeId[]),
  ...(Object.keys(ABILITIES) as AbilityId[]),
];

export interface DraftState {
  readonly config: BoardConfig;
  /** Remaining points per seat. */
  readonly budgets: readonly [number, number];
  /** Each seat's drafted deck so far. */
  readonly decks: readonly [DeckBuild, DeckBuild];
  /** The shared face-up pool (distinct card types). */
  readonly pool: readonly CardTypeId[];
  /** Whose pick it is. */
  readonly turn: PlayerId;
  /** A seat that has voluntarily finished drafting. */
  readonly passed: readonly [boolean, boolean];
  /**
   * The single card that will slide into the pool the next time a pick opens a
   * slot — shown to both seats as an "on deck" preview. It's about to be public
   * regardless, so revealing one card leaks nothing; the rest of the bag stays
   * unrolled (and unknowable) so the draft can't be read many picks ahead.
   */
  readonly nextUp: CardTypeId | null;
  /** The most recent pick (seat + card), for a brief "they took X" flash. */
  readonly lastPick: { readonly seat: PlayerId; readonly card: CardTypeId } | null;
  /** Cursor for the seeded RNG (pool draws/refills are reproducible). */
  readonly rngState: number;
  readonly phase: "drafting" | "done";
}

export type DraftResult =
  | { readonly ok: true; readonly state: DraftState }
  | { readonly ok: false; readonly error: string };

const other = (p: PlayerId): PlayerId => (p === 0 ? 1 : 0);

/** Can `player` legally take `card` right now (affordable and under its cap)? */
export function canTake(state: DraftState, player: PlayerId, card: CardTypeId): boolean {
  if (cardTypeCost(card) > state.budgets[player]) return false;
  const max = cardMaxCopies(card);
  return max === undefined || (state.decks[player][card] ?? 0) < max;
}

/** A seat that cannot take ANY draftable card has nothing left to draft. */
function exhausted(state: DraftState, player: PlayerId): boolean {
  return !DRAFTABLE.some((c) => canTake(state, player, c));
}

/** Draw one random card type not already present in `exclude`. */
function drawCard(rng: number, exclude: ReadonlySet<CardTypeId>): [CardTypeId, number] {
  const choices = DRAFTABLE.filter((c) => !exclude.has(c));
  const [next, idx] = nextInt(rng, choices.length);
  return [choices[idx]!, next];
}

/** Top the pool back up to POOL_SIZE with distinct card types. */
function refill(pool: CardTypeId[], rng: number): [CardTypeId[], number] {
  const next = pool.slice();
  const seen = new Set(next);
  let s = rng;
  while (next.length < POOL_SIZE && seen.size < DRAFTABLE.length) {
    const [card, ns] = drawCard(s, seen);
    s = ns;
    next.push(card);
    seen.add(card);
  }
  return [next, s];
}

/**
 * Roll the single "on deck" card: a distinct type not already in the pool (or
 * null in the impossible case that every draftable type is on show). Threaded
 * through the same seeded RNG, so it's reproducible like every other draw.
 */
function rollNextUp(pool: readonly CardTypeId[], rng: number): [CardTypeId | null, number] {
  const choices = DRAFTABLE.filter((c) => !pool.includes(c));
  if (choices.length === 0) return [null, rng];
  const [next, idx] = nextInt(rng, choices.length);
  return [choices[idx]!, next];
}

/**
 * Settle whose turn it is after a pick/pass, redraw the pool if the player to
 * move has nothing takeable in it, and mark the draft done when both seats are
 * finished. A seat counts as finished when it has passed or is exhausted.
 */
function settle(state: DraftState): DraftState {
  const fin0 = state.passed[0] || exhausted(state, 0);
  const fin1 = state.passed[1] || exhausted(state, 1);
  if (fin0 && fin1) return { ...state, phase: "done" };

  // Hand the turn to the opponent if they can still act, else keep it.
  const opp = other(state.turn);
  const oppFinished = opp === 0 ? fin0 : fin1;
  let next: DraftState = { ...state, turn: oppFinished ? state.turn : opp };

  // Guarantee the player to move has a takeable card in the pool (they aren't
  // finished, so something is affordable — reshuffle until the pool shows it).
  let guard = 0;
  while (
    guard++ < 64 &&
    !next.pool.some((c) => canTake(next, next.turn, c))
  ) {
    const [pool, rng] = refill([], next.rngState);
    const [nextUp, rng2] = rollNextUp(pool, rng);
    next = { ...next, pool, nextUp, rngState: rng2 };
  }
  return next;
}

/** Start a fresh draft from a seed. */
export function createDraft(
  seed: number,
  config: BoardConfig = { ...DEFAULT_CONFIG, budget: DRAFT_BUDGET },
): DraftState {
  const [pool, rng] = refill([], seed >>> 0);
  const [nextUp, rng2] = rollNextUp(pool, rng);
  // At full budget every card is affordable, so the opening pool is always
  // playable and seat 0 leads — no need to settle (which would hand off the turn).
  return {
    config,
    budgets: [config.budget, config.budget],
    decks: [{}, {}],
    pool,
    nextUp,
    lastPick: null,
    turn: 0,
    passed: [false, false],
    rngState: rng2,
    phase: "drafting",
  };
}

/** Take a card from the pool into `player`'s deck. */
export function draftPick(state: DraftState, player: PlayerId, card: CardTypeId): DraftResult {
  if (state.phase !== "drafting") return { ok: false, error: "The draft is over." };
  if (state.turn !== player) return { ok: false, error: "It isn't your pick." };
  if (!state.pool.includes(card)) return { ok: false, error: "That card isn't in the pool." };
  if (!canTake(state, player, card)) return { ok: false, error: "You can't take that card." };

  const decks = state.decks.slice() as [DeckBuild, DeckBuild];
  decks[player] = { ...decks[player], [card]: (decks[player][card] ?? 0) + 1 };
  const budgets = state.budgets.slice() as [number, number];
  budgets[player] = budgets[player] - cardTypeCost(card);

  // Remove the taken card, slide the previewed "on deck" card into the opened
  // slot, then top up (a no-op when nextUp filled it) and roll the next preview.
  let rng = state.rngState;
  let base = state.pool.filter((c) => c !== card);
  if (state.nextUp != null && !base.includes(state.nextUp)) base = [...base, state.nextUp];
  let pool: CardTypeId[];
  [pool, rng] = refill(base, rng);
  let nextUp: CardTypeId | null;
  [nextUp, rng] = rollNextUp(pool, rng);

  return {
    ok: true,
    state: settle({
      ...state,
      decks,
      budgets,
      pool,
      nextUp,
      lastPick: { seat: player, card },
      rngState: rng,
    }),
  };
}

/** Voluntarily finish drafting (keep the deck you have). */
export function draftPass(state: DraftState, player: PlayerId): DraftResult {
  if (state.phase !== "drafting") return { ok: false, error: "The draft is over." };
  if (state.turn !== player) return { ok: false, error: "It isn't your pick." };
  const passed = state.passed.slice() as [boolean, boolean];
  passed[player] = true;
  return { ok: true, state: settle({ ...state, passed, lastPick: null }) };
}

/** The two finished decks, ready for `createInitialState`. */
export function draftToBuilds(state: DraftState): [DeckBuild, DeckBuild] {
  return [state.decks[0], state.decks[1]];
}

const TERRAIN_PIECES: readonly PieceTypeId[] = ["hill", "valley", "ridge"];

function isTerrain(card: CardTypeId): boolean {
  return TERRAIN_PIECES.includes(card as PieceTypeId);
}

/** Is this a deployable, board-contesting fighter (not terrain, not a spell)? */
function isFighterCard(card: CardTypeId): boolean {
  if (!(card in PIECES)) return false;
  const def = PIECES[card as PieceTypeId];
  return def.deployable && def.value > 0;
}

/**
 * Material-ish base weight that seeds the draft score. Terrain has no material
 * value but real board-control value, so it gets a small floor — otherwise the
 * AI would never start a wall, and the terrain synergy below could never fire.
 */
function baseValue(card: CardTypeId): number {
  if (card in PIECES) {
    const v = PIECES[card as PieceTypeId].value;
    return v > 0 ? v : 1.5;
  }
  return 2; // abilities: a modest flat utility
}

function packOf(card: CardTypeId): PackId | undefined {
  return card in PIECES
    ? packOfPiece(card as PieceTypeId)
    : packOfAbility(card as AbilityId);
}

/** Sum the counts of cards in `deck` matching `pred`. */
function deckSum(deck: DeckBuild, pred: (c: CardTypeId) => boolean): number {
  let n = 0;
  for (const [c, k] of Object.entries(deck) as [CardTypeId, number][]) {
    if ((k ?? 0) > 0 && pred(c)) n += k!;
  }
  return n;
}

/**
 * The AI's draft score for a candidate: material value plus a few transparent,
 * synergy- and curve-aware nudges. Every term reads only from the deterministic
 * DraftState (both decks are public), so the pick stays a pure, seeded function
 * — the tiebreak in `chooseDraftPick` is the only RNG it touches.
 */
function scoreDraftPick(state: DraftState, player: PlayerId, card: CardTypeId): number {
  const deck = state.decks[player];
  const oppDeck = state.decks[other(player)];
  const cost = cardTypeCost(card);
  let score = baseValue(card) * 2;

  // CURVE: early on, prize cheap fighters so you have things to deploy turn one;
  // and don't hoard expensive bombs you can't get onto the board in time.
  if (cost <= 2 && isFighterCard(card) && deckSum(deck, isFighterCard) < 5) {
    score += 2;
  }
  if (cost >= 5) {
    score -= deckSum(deck, (c) => cardTypeCost(c) >= 5) * 2;
  }

  // COHESION: reward building into the section you're already invested in, so the
  // AI ends up with a themed army instead of a value pile.
  const pack = packOf(card);
  if (pack) score += Math.min(deckSum(deck, (c) => packOf(c) === pack), 4) * 0.6;

  // TERRAIN INVARIANT: never strand a wall — Recall is the only way to move or
  // clear terrain, so grab it once you own terrain, and don't pile on terrain
  // before you can support it.
  const myTerrain = deckSum(deck, isTerrain);
  const haveRecall = (deck.recall ?? 0) > 0;
  if (card === "recall" && myTerrain > 0 && !haveRecall) score += 6;
  if (isTerrain(card) && myTerrain > 0 && !haveRecall) score -= 2;

  // ANSWERS: Demolish / Recall are worth more when the opponent commits to terrain.
  if ((card === "demolish" || card === "recall") && deckSum(oppDeck, isTerrain) > 0) {
    score += 2;
  }

  return score;
}

/**
 * The computer's pick: take the highest-scoring takeable card in the pool (see
 * `scoreDraftPick`), breaking exact ties with the seeded RNG. Returns null only
 * if it has no legal pick (it would then pass). The AI drafts until exhausted.
 */
export function chooseDraftPick(state: DraftState, player: PlayerId): CardTypeId | null {
  const takeable = state.pool.filter((c) => canTake(state, player, c));
  if (takeable.length === 0) return null;
  let best = takeable[0]!;
  let bestVal = scoreDraftPick(state, player, best);
  let rng = state.rngState;
  for (const c of takeable.slice(1)) {
    const v = scoreDraftPick(state, player, c);
    let better = v > bestVal;
    if (v === bestVal) {
      const [next, r] = nextInt(rng, 2);
      rng = next;
      better = r === 1;
    }
    if (better) {
      best = c;
      bestVal = v;
    }
  }
  return best;
}
